import axios from "@utilities/axios";
import { isAxiosError } from "axios";

/**
 * pts client for the in-chat support banners (24h compensation, 48h
 * escalation). Contract: pts `ai_docs/SPECS/SPEC_CustomerCare_Escalation.md` §5.
 *
 * Every call carries the customer's pts license token (the axios
 * interceptor adds `Authorization: Bearer`) and is keyed on the Customer
 * Care conversation id, which is what the chat knows. A pts error arrives
 * as that error's HTTP status (SelfcareApi::executeJob sets it from the
 * exception code) with `{ code, message }` in the body, so axios throws:
 * `toPtsError` turns that into a PtsError carrying the code the component
 * decides on (403/404/410 hide the banner, 429/503 offer a retry). A
 * network failure is rethrown as is, for the offline sheet.
 */

export type CompensationStatus = "not_available" | "available" | "claimed";
export type EscalationStatus = "not_available" | "available" | "escalated";

export interface TicketState {
  conversation_id: number;
  ticket_id: number | null;
  compensation: {
    status: CompensationStatus;
    /** BNE; `null` when not available. Never a fixed number: 15 % of the
     *  last purchase, so show what comes back. */
    amount: number | null;
    currency: string;
    /** Only while `available`: pass as `t` to the redeem endpoint. */
    claim_token?: string;
    claim_url?: string;
  };
  escalation: {
    status: EscalationStatus;
    escalated_at: string | null;
  };
}

export class PtsError extends Error {
  code: number;
  constructor(code: number, message?: string) {
    super(message ?? `PTS_${code}`);
    this.name = "PtsError";
    this.code = code;
  }
}

const EMPTY: Omit<TicketState, "conversation_id"> = {
  ticket_id: null,
  compensation: { status: "not_available", amount: null, currency: "BNE" },
  escalation: { status: "not_available", escalated_at: null },
};

const codeOf = (data: unknown): number => {
  const raw = (data as { code?: unknown } | null)?.code;
  const code = Number(raw);
  return Number.isFinite(code) ? code : 0;
};

const messageOf = (data: unknown): string | undefined => {
  const message = (data as { message?: unknown } | null)?.message;
  return typeof message === "string" && message !== "" ? message : undefined;
};

/** An axios rejection from pts → PtsError (body `code`, else the HTTP status). */
const toPtsError = (error: unknown): never => {
  if (isAxiosError(error) && error.response !== undefined) {
    const bodyCode = codeOf(error.response.data);
    throw new PtsError(
      bodyCode === 0 ? error.response.status : bodyCode,
      messageOf(error.response.data) ?? error.message,
    );
  }
  throw error;
};

/** pts selfcare endpoints take their parameters from the query string or a
 *  form body; the axios instance is set up for multipart form bodies. */
const form = (fields: Record<string, string>): FormData => {
  const body = new FormData();
  for (const [name, value] of Object.entries(fields)) body.append(name, value);
  return body;
};

export const fetchTicketState = async (
  conversationId: string,
): Promise<TicketState> => {
  const response = await axios
    .post<
      Partial<TicketState>
    >("/ticket_compensation_state/", form({ conversation_id: conversationId }))
    .catch(toPtsError);
  const data = response.data;
  // Defensive: a 200 whose body still carries an error code.
  const code = codeOf(data);
  if (code >= 400) {
    throw new PtsError(code, messageOf(data));
  }
  return {
    conversation_id: Number(conversationId),
    ticket_id: data.ticket_id ?? EMPTY.ticket_id,
    compensation: { ...EMPTY.compensation, ...data.compensation },
    escalation: { ...EMPTY.escalation, ...data.escalation },
  };
};

export type ClaimResult = "done" | "already_redeemed";

/** The existing pts redeem endpoint, the same one the hidden notification's
 *  link opens. `Accept: application/json` (axios default here) makes it
 *  answer JSON instead of its browser page. */
export const claimCompensation = async (
  claimToken: string,
): Promise<{ result: ClaimResult; amount: number | null }> => {
  const response = await axios
    .post<{
      message_status?: string;
      amount?: number;
      code?: unknown;
      message?: string;
    }>("/ticket_compensation/", form({ action: "redeem", t: claimToken }))
    .catch(toPtsError);
  const data = response.data;
  if (
    data.message_status === "done" ||
    data.message_status === "already_redeemed"
  ) {
    return {
      result: data.message_status,
      amount: typeof data.amount === "number" ? data.amount : null,
    };
  }
  const code = codeOf(data);
  throw new PtsError(code === 0 ? 500 : code, messageOf(data));
};

export type EscalateResult = "done" | "already_escalated";

export const escalateToManagement = async (
  conversationId: string,
): Promise<{ result: EscalateResult; state: TicketState }> => {
  const response = await axios
    .post<
      Partial<TicketState> & { message_status?: string; message?: string }
    >("/ticket_escalation/", form({ action: "escalate", conversation_id: conversationId }))
    .catch(toPtsError);
  const data = response.data;
  if (
    data.message_status === "done" ||
    data.message_status === "already_escalated"
  ) {
    return {
      result: data.message_status,
      state: {
        conversation_id: Number(conversationId),
        ticket_id: data.ticket_id ?? EMPTY.ticket_id,
        compensation: { ...EMPTY.compensation, ...data.compensation },
        escalation: { ...EMPTY.escalation, ...data.escalation },
      },
    };
  }
  const code = codeOf(data);
  throw new PtsError(code === 0 ? 500 : code, messageOf(data));
};

/** "3.75", "0.71", "22.35": two decimals, as pts formats the amount. */
export const formatBne = (amount: number | null): string =>
  amount === null ? "" : amount.toFixed(2);
